import React from "react";
import ReactDOM from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import "@/index.css";
import App from "@/App";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 60_000,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(document.getElementById("root"));

// Capture a household invite code the INSTANT the app loads — before React renders, before auth
// state is known, and before React Router can redirect /join?code= to /. localStorage written here
// is the single source of truth the accept flow reads, so it surfaces reliably for BOTH signed-in
// and signed-out users (the real founder failure: an existing user who was already signed in, so
// the signed-out Auth screen — the only other place that captured the code — never mounted).
try {
  const params = new URLSearchParams(window.location.search);
  const code = (params.get("code") || "").trim().toUpperCase();
  if (window.location.pathname.includes("/join") && code) {
    localStorage.setItem("heldall_pending_invite", code);
  }
} catch (_) { /* localStorage unavailable — JoinInviteSheet falls back to the URL code */ }

root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
    </QueryClientProvider>
  </React.StrictMode>,
);

// Register service worker for PWA / home-screen install + prompt-on-update.
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    // updateViaCache:"none" → the SW script is always fetched bypassing the HTTP cache,
    // so a newly deployed service worker (and its new bundle) is discovered promptly.
    navigator.serviceWorker
      .register("/service-worker.js", { updateViaCache: "none" })
      .then((reg) => {
        let prompted = false;
        // Quiet, non-forcing "refresh to update" — only when a NEW version installs behind
        // an already-controlling SW (i.e. not on the very first install).
        const promptUpdate = () => {
          if (prompted || !navigator.serviceWorker.controller) return;
          prompted = true;
          import("sonner").then(({ toast }) => {
            toast("A new version of Heldall is ready.", {
              duration: Infinity,
              action: { label: "Refresh", onClick: () => window.location.reload() },
            });
          });
        };
        if (reg.waiting) promptUpdate();
        reg.addEventListener("updatefound", () => {
          const nw = reg.installing;
          if (nw) nw.addEventListener("statechange", () => {
            if (nw.state === "installed") promptUpdate();
          });
        });
        // Re-check for a new deploy whenever the tab regains focus.
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "visible") reg.update().catch(() => {});
        });
      })
      .catch(() => {});
  });
}
